import axios from 'axios'
import * as Sentry from '@sentry/react'

import { doitReessayer, delaiAvantReessai, lireRetryAfter } from './apiRetry'
import {
  apiSembleHorsService,
  cleEndpoint,
  doitAnnoncerPanne,
  signalerEchec,
  signalerSucces,
} from './apiHealth'
import { useToastStore } from '../stores/useToastStore'

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '',
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
  withCredentials: true,
})

// Mutex pour dédupliquer les appels de refresh concurrents
let refreshPromise = null

const attendre = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

function avertirUtilisateur() {
  useToastStore.getState().addToast({
    message: 'Service momentanément indisponible. Certaines données peuvent manquer.',
    type: 'warning',
  })
}

api.interceptors.response.use(
  (response) => {
    signalerSucces()
    return response
  },
  async (error) => {
    const originalRequest = error.config
    const isRefreshRequest = originalRequest.url?.includes('/api/auth/refresh')

    if (error.response?.status === 401 && !originalRequest._retry && !isRefreshRequest) {
      originalRequest._retry = true

      // Si un refresh est déjà en cours, attendre son résultat
      if (!refreshPromise) {
        refreshPromise = import('../stores/useAuthStore')
          .then(({ useAuthStore }) => useAuthStore.getState().refreshAccessToken())
          .finally(() => { refreshPromise = null })
      }

      const success = await refreshPromise

      if (success) {
        const { useAuthStore } = await import('../stores/useAuthStore')
        const token = useAuthStore.getState().accessToken
        originalRequest.headers['Authorization'] = `Bearer ${token}`
        return api(originalRequest)
      }
    }

    const status = error.response?.status
    const cle = cleEndpoint(originalRequest?.url)

    // ---- Réessai des échecs transitoires ----
    //
    // Un 503 de `limit_req` ou un 502 d'amont qui redémarre ne sont pas des
    // pannes : ce sont des « reviens dans un instant ». Seules les méthodes
    // idempotentes sont rejouées — rejouer un POST enregistrerait deux fois.
    const tentative = (originalRequest._reessais ?? 0) + 1
    if (doitReessayer({ method: originalRequest?.method, status, attempt: tentative })) {
      originalRequest._reessais = tentative
      const delai = delaiAvantReessai({
        attempt: tentative,
        retryAfterMs: lireRetryAfter(error.response?.headers),
      })
      Sentry.addBreadcrumb({
        category: 'http.retry',
        level: 'info',
        message: `${status} sur ${cle} — réessai ${tentative} dans ${delai} ms`,
      })
      await attendre(delai)
      return api(originalRequest)
    }

    // ---- Sévérité de ce qui reste après les réessais ----
    //
    // Trois classes, délibérément distinctes. Les confondre est ce qui a fait
    // clore ce ticket une première fois sans rien corriger : tout en `error`
    // noie le signal, tout en silence laisse passer une vraie panne.
    if (status === 429) {
      // Limitation de débit : le client va trop vite, le serveur va bien.
      Sentry.addBreadcrumb({
        category: 'http.throttle',
        level: 'info',
        message: `429 sur ${cle} après ${tentative - 1} réessai(s)`,
      })
    } else if (status >= 500) {
      signalerEchec(cle)
      const panneGenerale = apiSembleHorsService()
      Sentry.captureException(error, {
        level: panneGenerale ? 'error' : 'warning',
        tags: { 'api.endpoint': cle, 'api.panne': panneGenerale ? 'generale' : 'isolee' },
      })
      // L'indisponibilité d'un endpoint de confort se tait ; celle de l'API
      // entière se voit, sous peine de laisser l'utilisateur devant une carte
      // muette sans explication.
      if (doitAnnoncerPanne()) avertirUtilisateur()
    }

    return Promise.reject(error)
  }
)

export default api
